import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Phone,
  Mail,
  MessageCircle,
  Video,
  CalendarPlus,
  StickyNote,
  Bell,
  Plus,
  Clock,
  Trash2,
  ExternalLink,
  X,
} from "lucide-react";
import api from "@/lib/api";
import { STATUSES, STATUS_BADGE, formatDate, formatTime, relativeFr, cn } from "@/lib/utils";
import RelanceButton from "@/components/RelanceButton";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { translateFr as tfr } from "@/i18n";

const TYPE_ICONS = {
  call: Phone,
  email: Mail,
  whatsapp: MessageCircle,
  video: Video,
  meeting: CalendarPlus,
  note: StickyNote,
};

export default function PlayerSheet({ playerId, open, onOpenChange, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [club, setClub] = useState(null);
  const [newInter, setNewInter] = useState({ type: "note", note: "" });
  const [newRem, setNewRem] = useState({ due_at: "", note: "" });

  const load = async () => {
    if (!playerId) return;
    setLoading(true);
    try {
      const r = await api.get(`/players/${playerId}`);
      setData(r.data);
      // try to find club for relance contact info
      if (r.data?.player?.club) {
        const cs = await api.get("/clubs", { params: { q: r.data.player.club } });
        const found = cs.data?.find((c) => c.name === r.data.player.club);
        setClub(found || null);
      }
    } catch (e) {
      toast.error(tfr("Impossible de charger le joueur"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && playerId) { setData(null); setClub(null); load(); }
    /* eslint-disable-next-line */
  }, [open, playerId]);

  if (!open) return null;
  const player = data?.player;

  const updateStatus = async (status) => {
    await api.patch(`/players/${playerId}/status`, { status });
    setData((d) => ({ ...d, player: { ...d.player, status } }));
    onUpdated?.();
  };

  const addInteraction = async () => {
    if (!newInter.note.trim()) { toast.error("Note vide"); return; }
    await api.post("/interactions", { player_id: playerId, ...newInter });
    setNewInter({ type: "note", note: "" });
    toast.success(tfr("Interaction ajoutée"));
    load(); onUpdated?.();
  };

  const addReminder = async () => {
    if (!newRem.due_at) { toast.error("Date requise"); return; }
    await api.post("/reminders", { player_id: playerId, due_at: new Date(newRem.due_at).toISOString(), note: newRem.note });
    setNewRem({ due_at: "", note: "" });
    toast.success(tfr("Rappel créé"));
    load();
  };

  const remove = async () => {
    if (!window.confirm(tfr("Supprimer ce joueur définitivement ?"))) return;
    await api.delete(`/players/${playerId}`);
    toast.success(tfr("Joueur supprimé"));
    onOpenChange(false);
    onUpdated?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto scroll-thin p-0"
        data-testid="player-sheet"
        hideClose
      >
        {!player ? (
          <>
            <SheetHeader className="p-6 border-b border-border">
              <SheetTitle className="text-2xl tracking-tight">{tfr("Fiche joueur")}</SheetTitle>
              <SheetDescription>{tfr("Chargement…")}</SheetDescription>
            </SheetHeader>
            <div className="p-6 text-sm text-muted-foreground">{loading ? "Chargement…" : ""}</div>
          </>
        ) : (
          <div className="flex flex-col">
            <SheetHeader className="relative p-6 border-b border-border">
              <SheetClose
                aria-label={tfr("Fermer")}
                data-testid="player-sheet-close"
                className="absolute top-4 right-4 z-50 inline-flex h-11 w-11 xl:h-10 xl:w-10 aspect-square shrink-0 cursor-pointer items-center justify-center rounded-[10px] transition-all duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20 bg-white border border-[#E5E7EB] text-[#111827] hover:bg-[#F3F4F6] dark:bg-white/[0.06] dark:border-white/[0.12] dark:text-white/85 dark:hover:bg-white/[0.12] dark:hover:text-white"
              >
                <X className="h-[18px] w-[18px] opacity-90" strokeWidth={2} />
              </SheetClose>

              <div className="flex flex-col items-center text-center px-8">
                <SheetTitle className="text-[clamp(22px,2.5vw,28px)] font-semibold leading-[1.15] tracking-[-0.01em] line-clamp-2 break-words">
                  {player.name}
                </SheetTitle>
                <SheetDescription className="mt-1 text-sm text-muted-foreground dark:text-white/60">
                  {player.position}{player.position && player.club ? " · " : ""}{player.club}{player.age ? ` · ${player.age} ans` : ""}{player.level ? ` · ${player.level}` : ""}
                </SheetDescription>

                <div className="flex justify-center items-center gap-3 mt-4 flex-wrap">
                  <Button
                    asChild
                    className="gap-2 active:scale-[0.97] transition-transform duration-150"
                    data-testid="open-full-profile"
                  >
                    <Link
                      to={`/players/${player.id}`}
                      onClick={() => onOpenChange?.(false)}
                      title={tfr("Voir la fiche complète")}
                    >
                      <ExternalLink className="h-4 w-4" /> Fiche
                    </Link>
                  </Button>
                  <RelanceButton player={player} club={club} size="full" context="sheet" />
                </div>
              </div>

              {(player.phone || player.email) ? (
                <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-1 mt-4 text-xs">
                  {player.phone && (
                    <a
                      href={`tel:${player.phone.replace(/\s/g, "")}`}
                      className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                      data-testid="player-phone-link"
                    >
                      <Phone className="h-3 w-3" /> {player.phone}
                    </a>
                  )}
                  {player.email && (
                    <a
                      href={`mailto:${player.email}`}
                      className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                      data-testid="player-email-link"
                    >
                      <Mail className="h-3 w-3" /> {player.email}
                    </a>
                  )}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground mt-4 italic text-center">{tfr("Contact joueur non renseigné")}</div>
              )}
              <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                <Select value={player.status} onValueChange={updateStatus}>
                  <SelectTrigger className="w-44 h-8" data-testid="player-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Badge variant="outline" className={cn("border", STATUS_BADGE[player.status])}>
                  {tfr(player.status)}
                </Badge>
                <span className="text-xs text-muted-foreground sm:ml-auto">
                  {tfr("Dernière interaction")}: {relativeFr(player.last_interaction_at)}
                </span>
              </div>
            </SheetHeader>

            <Tabs defaultValue="timeline" className="px-6 py-4">
              <TabsList>
                <TabsTrigger value="timeline" data-testid="tab-timeline">{tfr("Historique")}</TabsTrigger>
                <TabsTrigger value="reminders" data-testid="tab-reminders">{tfr("Rappels")}</TabsTrigger>
                <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
              </TabsList>

              <TabsContent value="timeline" className="space-y-4 mt-4">
                <div className="space-y-2 rounded-lg border border-border bg-surface p-3">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{tfr("Ajouter une interaction")}</Label>
                  <div className="flex gap-2">
                    <Select value={newInter.type} onValueChange={(v) => setNewInter({ ...newInter, type: v })}>
                      <SelectTrigger className="w-36" data-testid="new-interaction-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="call">Appel</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="video">{tfr("Vidéo")}</SelectItem>
                        <SelectItem value="meeting">{tfr("Rendez-vous")}</SelectItem>
                        <SelectItem value="note">Note</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      value={newInter.note}
                      onChange={(e) => setNewInter({ ...newInter, note: e.target.value })}
                      placeholder={tfr("Détail de l'interaction…")}
                      data-testid="new-interaction-note"
                    />
                    <Button onClick={addInteraction} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-interaction-submit">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  {data.interactions?.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">{tfr("Aucune interaction.")}</div>
                  )}
                  {data.interactions?.map((i) => {
                    const Ic = TYPE_ICONS[i.type] || StickyNote;
                    return (
                      <div key={i.id} className="flex gap-3 items-start" data-testid={`interaction-${i.id}`}>
                        <div className="mt-0.5 rounded-full bg-secondary p-1.5">
                          <Ic className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm">{i.note}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {formatDate(i.occurred_at)} · {formatTime(i.occurred_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="reminders" className="space-y-4 mt-4">
                <div className="space-y-2 rounded-lg border border-border bg-surface p-3">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{tfr("Nouveau rappel")}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="datetime-local"
                      value={newRem.due_at}
                      onChange={(e) => setNewRem({ ...newRem, due_at: e.target.value })}
                      className="w-56"
                      data-testid="new-reminder-date"
                    />
                    <Input
                      value={newRem.note}
                      onChange={(e) => setNewRem({ ...newRem, note: e.target.value })}
                      placeholder="Note"
                      data-testid="new-reminder-note"
                    />
                    <Button onClick={addReminder} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-reminder-submit">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {data.reminders?.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">{tfr("Aucun rappel.")}</div>
                  )}
                  {data.reminders?.map((r) => {
                    const overdue = !r.completed && new Date(r.due_at) < new Date();
                    return (
                      <div key={r.id} className={cn(
                        "flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2",
                        overdue && "border-danger/40"
                      )} data-testid={`reminder-${r.id}`}>
                        <Bell className={cn("h-4 w-4", overdue ? "text-danger" : "text-muted-foreground")} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm">{r.note || tfr("Rappel")}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(r.due_at)} · {formatTime(r.due_at)}
                            {overdue && <span className="text-danger ml-1">{tfr("en retard")}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="notes" className="mt-4">
                <PlayerNotes player={player} onSaved={load} />
              </TabsContent>
            </Tabs>

            <div className="px-6 py-4 border-t border-border mt-auto">
              <Button variant="ghost" onClick={remove} className="text-danger hover:text-danger gap-2" data-testid="player-delete-btn">
                <Trash2 className="h-4 w-4" /> {tfr("Supprimer")}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function PlayerNotes({ player, onSaved }) {
  const [notes, setNotes] = useState(player.notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/players/${player.id}`, { notes });
      toast.success(tfr("Notes enregistrées"));
      onSaved?.();
    } catch { toast.error(tfr("Erreur")); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-2">
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={8} placeholder={tfr("Notes libres sur le joueur…")} data-testid="player-notes-textarea" />
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="player-notes-save">{saving ? "…" : "Enregistrer"}</Button>
      </div>
    </div>
  );
}
